import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export interface CTASectionData {
  badge: string;
  titlePrefix: string;
  titleHighlight: string;
  subtitle: string;
  btnAcademyText: string;
  btnAcademyLink: string;
  btnConsultText: string;
  btnConsultLink: string;
  btnServicesText: string;
  btnServicesLink: string;
  trustIndicators: Array<{ icon: string; text: string }>;
}

export const defaultCTAData: CTASectionData = {
  badge: 'TRANSFORMA TU NEGOCIO',
  titlePrefix: '¿Listo para transformar tu ',
  titleHighlight: 'presencia digital?',
  subtitle: 'Descubre cómo nuestras soluciones integrales de marketing digital, branding y automatización con IA pueden escalar tu negocio.',
  btnAcademyText: 'Explorar Academia',
  btnAcademyLink: 'https://smartbusiness.academy',
  btnConsultText: 'Agendar Consultoría',
  btnConsultLink: '/contact',
  btnServicesText: 'Ver Servicios',
  btnServicesLink: '/services',
  trustIndicators: [
    { icon: 'ri-shield-check-line', text: '100% Personalizado' },
    { icon: 'ri-customer-service-line', text: 'Soporte Continuo' },
    { icon: 'ri-global-line', text: 'Enfoque Global' },
  ],
};

interface CTASectionProps {
  data?: CTASectionData;
  isEditing?: boolean;
  onChange?: (data: CTASectionData) => void;
}

const CTASection: React.FC<CTASectionProps> = ({
  data,
  isEditing = false,
  onChange,
}) => {
  const { t } = useTranslation();

  const cta = data || {
    badge: t('home.cta.badge') || defaultCTAData.badge,
    titlePrefix: t('home.cta.titlePrefix') || defaultCTAData.titlePrefix,
    titleHighlight: t('home.cta.titleHighlight') || defaultCTAData.titleHighlight,
    subtitle: t('home.cta.subtitle') || defaultCTAData.subtitle,
    btnAcademyText: t('home.cta.buttons.academy') || defaultCTAData.btnAcademyText,
    btnAcademyLink: defaultCTAData.btnAcademyLink,
    btnConsultText: t('home.cta.buttons.consult') || defaultCTAData.btnConsultText,
    btnConsultLink: defaultCTAData.btnConsultLink,
    btnServicesText: t('home.cta.buttons.services') || defaultCTAData.btnServicesText,
    btnServicesLink: defaultCTAData.btnServicesLink,
    trustIndicators: [
      { icon: 'ri-shield-check-line', text: (t('home.cta.indicators', { returnObjects: true }) as string[])?.[0] || '100% Personalizado' },
      { icon: 'ri-customer-service-line', text: (t('home.cta.indicators', { returnObjects: true }) as string[])?.[1] || 'Soporte Continuo' },
      { icon: 'ri-global-line', text: (t('home.cta.indicators', { returnObjects: true }) as string[])?.[2] || 'Enfoque Global' },
    ],
  };

  const handleIndicatorChange = (index: number, text: string) => {
    if (!onChange || !data) return;
    const updated = cta.trustIndicators.map((item, i) => (i === index ? { ...item, text } : item));
    onChange({ ...cta, trustIndicators: updated });
  };

  return (
    <section className="relative py-28 overflow-hidden bg-[#050008]">
      {/* Animated gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#1a0040] via-[#050008] to-[#001a2e]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_30%_50%,rgba(140,82,255,0.3)_0%,transparent_60%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_70%_50%,rgba(0,194,255,0.2)_0%,transparent_60%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_100%,rgba(255,45,170,0.15)_0%,transparent_50%)]" />

      {/* Decorative lines */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#8c52ff]/50 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#00c2ff]/30 to-transparent" />

      <div className="relative z-10 max-w-4xl mx-auto px-4 md:px-8 text-center w-full">
        <div className="inline-flex items-center gap-2 bg-white/5 border border-[#FF2DAA]/30 rounded-full px-4 py-2 mb-8">
          <span className="w-2 h-2 rounded-full bg-[#FF2DAA] animate-pulse" />
          {isEditing && onChange && data ? (
            <input
              type="text"
              value={cta.badge}
              onChange={(e) => onChange({ ...cta, badge: e.target.value })}
              className="bg-black/40 border border-pink-500/40 rounded px-2 text-[#FF2DAA] text-xs font-bold uppercase tracking-widest focus:outline-none"
            />
          ) : (
            <span className="text-[#FF2DAA] text-xs font-bold uppercase tracking-widest">{cta.badge}</span>
          )}
        </div>

        {isEditing && onChange && data ? (
          <div className="space-y-4 mb-8 max-w-2xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-left">
              <div>
                <label className="text-[11px] text-white/50 uppercase tracking-wider block mb-1">Prefijo de Título</label>
                <input
                  type="text"
                  value={cta.titlePrefix}
                  onChange={(e) => onChange({ ...cta, titlePrefix: e.target.value })}
                  className="w-full bg-black/40 border border-white/20 rounded-lg px-3 py-2 text-white font-black text-xl focus:outline-none"
                />
              </div>
              <div>
                <label className="text-[11px] text-pink-400 uppercase tracking-wider block mb-1">Título Destacado</label>
                <input
                  type="text"
                  value={cta.titleHighlight}
                  onChange={(e) => onChange({ ...cta, titleHighlight: e.target.value })}
                  className="w-full bg-black/40 border border-pink-500/40 rounded-lg px-3 py-2 text-pink-300 font-black text-xl focus:outline-none"
                />
              </div>
            </div>

            <div className="text-left">
              <label className="text-[11px] text-white/50 uppercase tracking-wider block mb-1">Subtítulo</label>
              <textarea
                value={cta.subtitle}
                onChange={(e) => onChange({ ...cta, subtitle: e.target.value })}
                rows={3}
                className="w-full bg-black/40 border border-purple-500/30 rounded-lg p-2.5 text-white/80 text-sm focus:outline-none"
              />
            </div>

            {/* Buttons Editor */}
            <div className="p-4 bg-white/5 border border-purple-500/20 rounded-xl space-y-3 text-left">
              <h4 className="text-xs font-bold text-purple-300 uppercase tracking-wider">Botones y Enlaces</h4>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="space-y-1">
                  <label className="text-[10px] text-white/40 block">Botón 1 (Principal)</label>
                  <input
                    type="text"
                    value={cta.btnAcademyText}
                    onChange={(e) => onChange({ ...cta, btnAcademyText: e.target.value })}
                    className="w-full bg-black/40 border border-white/10 rounded px-2 py-1 text-white text-xs focus:outline-none"
                    placeholder="Texto"
                  />
                  <input
                    type="text"
                    value={cta.btnAcademyLink}
                    onChange={(e) => onChange({ ...cta, btnAcademyLink: e.target.value })}
                    className="w-full bg-black/40 border border-white/10 rounded px-2 py-1 text-white/50 text-xs focus:outline-none"
                    placeholder="Enlace URL"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] text-white/40 block">Botón 2 (Consultoría)</label>
                  <input
                    type="text"
                    value={cta.btnConsultText}
                    onChange={(e) => onChange({ ...cta, btnConsultText: e.target.value })}
                    className="w-full bg-black/40 border border-white/10 rounded px-2 py-1 text-white text-xs focus:outline-none"
                    placeholder="Texto"
                  />
                  <input
                    type="text"
                    value={cta.btnConsultLink}
                    onChange={(e) => onChange({ ...cta, btnConsultLink: e.target.value })}
                    className="w-full bg-black/40 border border-white/10 rounded px-2 py-1 text-white/50 text-xs focus:outline-none"
                    placeholder="Enlace URL"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] text-white/40 block">Botón 3 (Servicios)</label>
                  <input
                    type="text"
                    value={cta.btnServicesText}
                    onChange={(e) => onChange({ ...cta, btnServicesText: e.target.value })}
                    className="w-full bg-black/40 border border-white/10 rounded px-2 py-1 text-white text-xs focus:outline-none"
                    placeholder="Texto"
                  />
                  <input
                    type="text"
                    value={cta.btnServicesLink}
                    onChange={(e) => onChange({ ...cta, btnServicesLink: e.target.value })}
                    className="w-full bg-black/40 border border-white/10 rounded px-2 py-1 text-white/50 text-xs focus:outline-none"
                    placeholder="Enlace URL"
                  />
                </div>
              </div>
            </div>
          </div>
        ) : (
          <>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6 leading-tight">
              {cta.titlePrefix}
              <span className="block bg-gradient-to-r from-[#8c52ff] via-[#FF2DAA] to-[#00c2ff] bg-clip-text text-transparent">
                {cta.titleHighlight}
              </span>
            </h2>

            <p className="text-white/60 text-base md:text-xl leading-relaxed mb-12 max-w-2xl mx-auto">
              {cta.subtitle}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {cta.btnAcademyLink.startsWith('http') ? (
                <a
                  href={cta.btnAcademyLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="whitespace-nowrap inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#8c52ff] to-[#FF2DAA] text-white font-bold px-8 py-4 rounded-full hover:opacity-90 hover:scale-105 transition-all duration-300 text-base shadow-lg shadow-purple-500/40 cursor-pointer"
                >
                  <i className="ri-graduation-cap-line" />
                  {cta.btnAcademyText}
                </a>
              ) : (
                <Link
                  to={cta.btnAcademyLink}
                  className="whitespace-nowrap inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#8c52ff] to-[#FF2DAA] text-white font-bold px-8 py-4 rounded-full hover:opacity-90 hover:scale-105 transition-all duration-300 text-base shadow-lg shadow-purple-500/40 cursor-pointer"
                >
                  <i className="ri-graduation-cap-line" />
                  {cta.btnAcademyText}
                </Link>
              )}

              {cta.btnConsultLink.startsWith('http') || cta.btnConsultLink.startsWith('mailto:') ? (
                <a
                  href={cta.btnConsultLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="whitespace-nowrap inline-flex items-center justify-center gap-2 bg-white/5 backdrop-blur-sm border border-white/20 text-white font-semibold px-8 py-4 rounded-full hover:bg-white/10 hover:border-[#00c2ff]/50 transition-all duration-300 text-base cursor-pointer"
                >
                  <i className="ri-calendar-line" />
                  {cta.btnConsultText}
                </a>
              ) : (
                <Link
                  to={cta.btnConsultLink}
                  className="whitespace-nowrap inline-flex items-center justify-center gap-2 bg-white/5 backdrop-blur-sm border border-white/20 text-white font-semibold px-8 py-4 rounded-full hover:bg-white/10 hover:border-[#00c2ff]/50 transition-all duration-300 text-base cursor-pointer"
                >
                  <i className="ri-calendar-line" />
                  {cta.btnConsultText}
                </Link>
              )}

              {cta.btnServicesLink.startsWith('http') ? (
                <a
                  href={cta.btnServicesLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="whitespace-nowrap inline-flex items-center justify-center gap-2 bg-white/5 backdrop-blur-sm border border-white/20 text-white font-semibold px-8 py-4 rounded-full hover:bg-white/10 hover:border-[#8c52ff]/50 transition-all duration-300 text-base cursor-pointer"
                >
                  <i className="ri-compass-3-line" />
                  {cta.btnServicesText}
                </a>
              ) : (
                <Link
                  to={cta.btnServicesLink}
                  className="whitespace-nowrap inline-flex items-center justify-center gap-2 bg-white/5 backdrop-blur-sm border border-white/20 text-white font-semibold px-8 py-4 rounded-full hover:bg-white/10 hover:border-[#8c52ff]/50 transition-all duration-300 text-base cursor-pointer"
                >
                  <i className="ri-compass-3-line" />
                  {cta.btnServicesText}
                </Link>
              )}
            </div>
          </>
        )}

        {/* Trust indicators */}
        <div className="flex flex-wrap items-center justify-center gap-6 mt-14">
          {cta.trustIndicators.map((item, i) => (
            <div key={i} className="flex items-center gap-2 text-white/40">
              <i className={`${item.icon} text-[#8c52ff]`} />
              {isEditing && onChange && data ? (
                <input
                  type="text"
                  value={item.text}
                  onChange={(e) => handleIndicatorChange(i, e.target.value)}
                  className="bg-black/40 border border-white/10 rounded px-2 py-0.5 text-xs text-white/70 focus:outline-none"
                />
              ) : (
                <span className="text-sm">{item.text}</span>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CTASection;
